import * as React from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { RedirectedProductListEntry } from 'contracts/redirected-products';
import { useRedirectedProductSapStatusOptions } from 'hooks/api/options';
import { useUpdateRedirectedProduct } from 'hooks/api/redirected-products';
import { Drawer } from 'components/ui/drawer';
import { Form, FormField } from 'components/ui/form';
import { ComboboxGs } from 'components/ui/Forms';
import { Button } from 'components/ui/ButtonGs';
import { ActivityIndicator } from 'styles/components';
import { ProductsTable } from 'pages/stock-inspection/shared-components';

import { formSchema, FormSchema } from './form-schema';

type Ref = {
  open: (selectedProduct: RedirectedProductListEntry) => void;
  close: () => void;
}

export type UpdateRedirectedProductSapStatusDrawerRef = Ref;

type OnSubmit = Parameters<
  ReturnType<typeof useUpdateRedirectedProduct>['updateRedirectedProduct']
>[0];

type Props = {
  onSubmit: (data: OnSubmit) => void;
  loading?: boolean;
}

export const UpdateRedirectedProductSapStatusDrawer = React.forwardRef<Ref, Props>(({
                                                                                      onSubmit,
                                                                                      loading,
                                                                                    }, ref) => {
  const [product, setProduct] = React.useState<RedirectedProductListEntry>();
  const [open, setOpen] = React.useState(false);

  const sapStatusOptions = useRedirectedProductSapStatusOptions();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
  });

  const currentSapStatus = useWatch({
    control: form.control,
    name: 'sapStatus',
    ...(product?.sapStatus && {
      defaultValue: product.sapStatus,
    }),
  });

  const sapStatusChanged = React.useMemo(() => {
    return `${product?.sapStatus}` !== currentSapStatus;
  }, [currentSapStatus, product?.sapStatus]);

  const onFormSubmit = React.useCallback((formData: FormSchema) => {
    if (!product) return;

    onSubmit({
      productId: product.id,
      payload: {
        sapStatus: formData.sapStatus,
      },
    });
  }, [onSubmit, product]);

  const onOpenChange = React.useCallback((open: boolean) => {
    setOpen(open);
    form.reset();
  }, [form, product]);

  const hydrateFormData = React.useCallback((product: RedirectedProductListEntry) => {
    const { sapStatus } = product;
    if (sapStatus) {
      form.setValue('sapStatus', sapStatus);
    }
  }, []);

  React.useImperativeHandle(ref, () => ({
    open: (product) => {
      onOpenChange(true);
      setProduct(product);
      hydrateFormData(product);
    },
    close: () => {
      onOpenChange(false);
      setProduct(undefined);
    },
  }), []);

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Content>
        <div className="max-w-[800px] w-full mx-auto">
          <Drawer.Header>
            <Drawer.Title className="text-center flex flex-col font-GilroyBold text-gray-600 text-[16px]">
              <span>Alterar motivo</span>
              <span className={'font-extrabold text-gray-800 text-xl'}>
                {product?.materialDescription}
              </span>
            </Drawer.Title>
          </Drawer.Header>
          {product && (
            <ProductsTable products={[product]} className={'mb-6'} />
          )}
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onFormSubmit)}
              className="flex flex-col gap-4 pb-10"
            >
              <div className={'grid grid-cols-1 gap-4'}>
                <FormField
                  control={form.control}
                  name={'sapStatus'}
                  render={({ field }) => (
                    <ComboboxGs
                      form={form}
                      fieldName={field.name}
                      fieldValue={field.value}
                      content={sapStatusOptions.singleSelectOptions}
                      label={'Status SAP'}
                      portal={false}
                    />
                  )}
                />
              </div>
              <div className={'grid grid-cols-2 gap-4'}>
                <Button.Root
                  type={'button'}
                  size={'sm'}
                  variant={'secondary'}
                  onClick={() => onOpenChange(false)}
                >
                  Cancelar
                </Button.Root>
                <Button.Root
                  type={'submit'}
                  size={'sm'}
                  disabled={loading || !sapStatusChanged}
                >
                  {loading ? <ActivityIndicator /> : 'Salvar'}
                </Button.Root>
              </div>
            </form>
          </Form>
        </div>
      </Drawer.Content>
    </Drawer.Root>
  );
});
UpdateRedirectedProductSapStatusDrawer.displayName = 'UpdateRedirectedProductSapStatusDrawer';